import React, { useState } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import { UserIcon, BookIcon } from '../components/Icons';

export const AgentProfilePage = () => {
  // Mock agent data - in real app, would fetch based on ID
  const agentId = 1;

  const [isFavorited, setIsFavorited] = useState(false);

  const agent = {
    id: 1,
    name: 'Rebecca Sterling',
    agency: 'Sterling Literary Agency',
    profilePhoto: null,
    status: 'open',
    bio: 'Rebecca Sterling has been a literary agent for over 15 years, with a passion for discovering new voices in literary and historical fiction. Before becoming an agent, she worked as an editor at a major publishing house, where she developed a keen eye for compelling narratives and beautiful prose.\n\nShe loves working closely with her authors on developing their craft and is known for her detailed editorial feedback. Rebecca has sold over 50 books to major publishers and has helped several debut authors achieve bestseller status.',
    genres: ['Literary Fiction', 'Historical Fiction', 'Memoir'],
    mswl: {
      seeking: [
        'Literary fiction with strong character development and emotional depth',
        'Historical fiction set in lesser-known time periods or featuring underrepresented perspectives',
        'Memoir with unique voice and compelling narrative arc',
        'Stories featuring immigrant experiences, family sagas, or intergenerational narratives',
        'Fiction with gorgeous prose that also tells a compelling story',
        'Books that make me think differently about the world',
      ],
      notSeeking: [
        'Genre fiction (fantasy, sci-fi, romance)',
        'Young adult or middle grade',
        'Poetry or short story collections',
        'Self-help or prescriptive non-fiction',
      ],
    },
    submissionGuidelines: {
      format: 'Email only',
      materials: 'Query letter + first 10 pages pasted in email body',
      synopsis: 'Optional but appreciated',
      exclusivity: 'Not required',
      multipleSubmissions: 'Accepted',
      responseTime: '6-8 weeks',
      responsePolicy: 'Responds to all queries',
    },
    contact: {
      email: 'submissions@sterlingliteraryagency.com',
      website: 'www.sterlingliteraryagency.com',
      twitter: '@RebeccaSterling',
      bluesky: '@rebeccasterling.bsky.social',
    },
    recentSales: [
      {
        title: 'The Archive of Forgotten Dreams',
        author: 'Maria Chen',
        publisher: 'Random House',
        year: 2023,
      },
      {
        title: 'Letters from the Old Country',
        author: 'Josef Kowalski',
        publisher: 'Penguin Press',
        year: 2023,
      },
      {
        title: 'The Memory Keeper\'s Daughter',
        author: 'Sarah Williams',
        publisher: 'HarperCollins',
        year: 2022,
      },
    ],
    clients: [
      'Maria Chen',
      'Josef Kowalski',
      'Sarah Williams',
      'David Park',
      'Elena Rodriguez',
      'And 10 more...',
    ],
    stats: {
      clientCount: 15,
      recentSales: 3,
      yearsExperience: 15,
      avgResponseTime: '6-8 weeks',
    },
  };

  const handleFavorite = () => {
    setIsFavorited(!isFavorited);
    window.alert(isFavorited ? 'Removed from favorites' : 'Added to favorites!');
  };

  const getStatusBadge = (status: string) => {
    return status === 'open'
      ? <Badge variant="success" size="large">Open to Queries</Badge>
      : <Badge variant="default" size="large">Closed to Queries</Badge>
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => window.location.href = '/agents'}
          style={{ marginBottom: '16px' }}
        >
          ← Back to Agent Directory
        </Button>

        {/* Agent Header */}
        <Card style={{ marginBottom: '32px' }}>
          <div style={{
            display: 'flex',
            gap: '24px',
            marginBottom: '24px',
          }}>
            {/* Profile Photo */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              backgroundColor: theme.colors.primaryLight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: theme.colors.primary,
            }}>
              <UserIcon width={50} height={50} />
            </div>

            {/* Header Info */}
            <div style={{ flex: 1 }}>
              <h1 style={{
                fontSize: '36px',
                fontWeight: '400',
                marginBottom: '8px',
                color: theme.colors.textPrimary,
              }}>
                {agent.name}
              </h1>
              <div style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
                marginBottom: '12px',
              }}>
                {agent.agency}
              </div>
              {getStatusBadge(agent.status)}
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}>
              <Button
                variant={isFavorited ? 'secondary' : 'outline'}
                onClick={handleFavorite}
              >
                {isFavorited ? '★ Favorited' : '☆ Save to Favorites'}
              </Button>
              {agent.status === 'open' && (
                <Button
                  onClick={() => window.location.href = `/writer/query/new?agent=${agentId}`}
                >
                  Submit Query
                </Button>
              )}
            </div>
          </div>

          {/* Stats Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '16px',
            padding: '20px',
            backgroundColor: theme.colors.backgroundAlt,
            borderRadius: theme.borderRadius.medium,
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '28px',
                fontWeight: '400',
                color: theme.colors.primary,
                fontFamily: theme.fonts.monospace,
                marginBottom: '4px',
              }}>
                {agent.stats.yearsExperience}
              </div>
              <div style={{
                fontSize: '13px',
                color: theme.colors.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                Years Experience
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '28px',
                fontWeight: '400',
                color: theme.colors.primary,
                fontFamily: theme.fonts.monospace,
                marginBottom: '4px',
              }}>
                {agent.stats.clientCount}
              </div>
              <div style={{
                fontSize: '13px',
                color: theme.colors.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                Clients
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '28px',
                fontWeight: '400',
                color: theme.colors.primary,
                fontFamily: theme.fonts.monospace,
                marginBottom: '4px',
              }}>
                {agent.stats.recentSales}
              </div>
              <div style={{
                fontSize: '13px',
                color: theme.colors.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                Recent Sales
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '16px',
                fontWeight: '500',
                color: theme.colors.primary,
                fontFamily: theme.fonts.monospace,
                marginBottom: '4px',
                marginTop: '6px',
              }}>
                {agent.stats.avgResponseTime}
              </div>
              <div style={{
                fontSize: '13px',
                color: theme.colors.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                Response Time
              </div>
            </div>
          </div>
        </Card>

        {/* Main Content Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: '32px',
        }}>
          {/* Left Column */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}>
            {/* Bio */}
            <Card>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '500',
                marginBottom: '16px',
                color: theme.colors.textPrimary,
              }}>
                About {agent.name}
              </h2>
              <p style={{
                fontSize: '16px',
                lineHeight: '1.8',
                color: theme.colors.textPrimary,
                whiteSpace: 'pre-wrap',
              }}>
                {agent.bio}
              </p>
            </Card>

            {/* Manuscript Wish List */}
            <Card>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '500',
                marginBottom: '16px',
                color: theme.colors.textPrimary,
              }}>
                Manuscript Wish List
              </h2>

              {/* Genres */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '500',
                  marginBottom: '12px',
                  color: theme.colors.textSecondary,
                }}>
                  Represents
                </h3>
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap',
                }}>
                  {agent.genres.map((genre, index) => (
                    <Badge key={index} variant="primary">
                      {genre}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Seeking */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '500',
                  marginBottom: '12px',
                  color: theme.colors.success,
                }}>
                  Currently Seeking
                </h3>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}>
                  {agent.mswl.seeking.map((item, index) => (
                    <li
                      key={index}
                      style={{
                        fontSize: '15px',
                        lineHeight: '1.6',
                        color: theme.colors.textPrimary,
                        paddingLeft: '24px',
                        position: 'relative',
                      }}
                    >
                      <span style={{
                        position: 'absolute',
                        left: '0',
                        color: theme.colors.success,
                      }}>
                        ✓
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Not Seeking */}
              <div>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '500',
                  marginBottom: '12px',
                  color: theme.colors.error,
                }}>
                  Not Seeking
                </h3>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}>
                  {agent.mswl.notSeeking.map((item, index) => (
                    <li
                      key={index}
                      style={{
                        fontSize: '15px',
                        lineHeight: '1.6',
                        color: theme.colors.textPrimary,
                        paddingLeft: '24px',
                        position: 'relative',
                      }}
                    >
                      <span style={{
                        position: 'absolute',
                        left: '0',
                        color: theme.colors.error,
                      }}>
                        ✗
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Card>

            {/* Submission Guidelines */}
            <Card>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '500',
                marginBottom: '16px',
                color: theme.colors.textPrimary,
              }}>
                Submission Guidelines
              </h2>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}>
                {Object.entries(agent.submissionGuidelines).map(([key, value]) => (
                  <div
                    key={key}
                    style={{
                      padding: '12px 16px',
                      backgroundColor: theme.colors.backgroundAlt,
                      borderRadius: theme.borderRadius.small,
                    }}
                  >
                    <div style={{
                      fontSize: '13px',
                      fontWeight: '500',
                      color: theme.colors.textSecondary,
                      marginBottom: '4px',
                      textTransform: 'capitalize',
                    }}>
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </div>
                    <div style={{
                      fontSize: '15px',
                      color: theme.colors.textPrimary,
                    }}>
                      {value}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Right Column */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}>
            {/* Contact Information */}
            <Card>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '500',
                marginBottom: '16px',
                color: theme.colors.textPrimary,
              }}>
                Contact Information
              </h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}>
                <div>
                  <div style={{
                    fontSize: '13px',
                    color: theme.colors.textSecondary,
                    marginBottom: '4px',
                  }}>
                    Email
                  </div>
                  <a
                    href={`mailto:${agent.contact.email}`}
                    style={{
                      fontSize: '14px',
                      color: theme.colors.primary,
                      textDecoration: 'none',
                      fontFamily: theme.fonts.monospace,
                    }}
                  >
                    {agent.contact.email}
                  </a>
                </div>
                <div>
                  <div style={{
                    fontSize: '13px',
                    color: theme.colors.textSecondary,
                    marginBottom: '4px',
                  }}>
                    Website
                  </div>
                  <a
                    href={`https://${agent.contact.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: '14px',
                      color: theme.colors.primary,
                      textDecoration: 'none',
                      fontFamily: theme.fonts.monospace,
                    }}
                  >
                    {agent.contact.website}
                  </a>
                </div>
                <div>
                  <div style={{
                    fontSize: '13px',
                    color: theme.colors.textSecondary,
                    marginBottom: '4px',
                  }}>
                    Twitter
                  </div>
                  <a
                    href={`https://twitter.com/${agent.contact.twitter.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: '14px',
                      color: theme.colors.primary,
                      textDecoration: 'none',
                      fontFamily: theme.fonts.monospace,
                    }}
                  >
                    {agent.contact.twitter}
                  </a>
                </div>
                <div>
                  <div style={{
                    fontSize: '13px',
                    color: theme.colors.textSecondary,
                    marginBottom: '4px',
                  }}>
                    Bluesky
                  </div>
                  <a
                    href={`https://bsky.app/profile/${agent.contact.bluesky.replace('@', '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontSize: '14px',
                      color: theme.colors.primary,
                      textDecoration: 'none',
                      fontFamily: theme.fonts.monospace,
                    }}
                  >
                    {agent.contact.bluesky}
                  </a>
                </div>
              </div>
            </Card>

            {/* Recent Sales */}
            <Card>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '500',
                marginBottom: '16px',
                color: theme.colors.textPrimary,
              }}>
                Recent Sales
              </h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}>
                {agent.recentSales.map((sale, index) => (
                  <div
                    key={index}
                    style={{
                      paddingBottom: index < agent.recentSales.length - 1 ? '16px' : '0',
                      borderBottom: index < agent.recentSales.length - 1 ? `1px solid ${theme.colors.border}` : 'none',
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      marginBottom: '6px',
                    }}>
                      <BookIcon width={16} height={16} />
                      <div style={{
                        fontSize: '15px',
                        fontWeight: '500',
                        color: theme.colors.textPrimary,
                        flex: 1,
                      }}>
                        {sale.title}
                      </div>
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: theme.colors.textSecondary,
                      paddingLeft: '28px',
                    }}>
                      by {sale.author}
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: theme.colors.textTertiary,
                      paddingLeft: '28px',
                      fontFamily: theme.fonts.monospace,
                    }}>
                      {sale.publisher} • {sale.year}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Client List */}
            <Card>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '500',
                marginBottom: '16px',
                color: theme.colors.textPrimary,
              }}>
                Select Clients
              </h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}>
                {agent.clients.map((client, index) => (
                  <div
                    key={index}
                    style={{
                      fontSize: '14px',
                      color: theme.colors.textPrimary,
                      paddingLeft: '16px',
                      position: 'relative',
                    }}
                  >
                    <span style={{
                      position: 'absolute',
                      left: '0',
                      color: theme.colors.primary,
                    }}>
                      •
                    </span>
                    {client}
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};
